/**
 * One row of the sidebar tree: a `treeitem` carrying the tree's roving tab
 * stop, the shared item menu openers (right-click, Shift+F10, long-press) and
 * the shared drag source and folder drop target props. The chevron is a
 * pointer shortcut only; keyboard users expand with Right and Left.
 */

import { Box, Flex, Text } from '@chakra-ui/react';
import type { CSSProperties, MouseEvent } from 'react';

import { getHierarchyNodeGlyph } from '../../../theme/entityIcons';
import { useIsAclEnforced } from '../../../hooks/data/useEntityAccess';
import { Glyph } from '../../ui/Glyph';
import { MaterialIcon } from '../../ui/MaterialIcon';
import { RestrictedBadge } from '../../ui/RestrictedBadge';
import type { ItemContextMenuProps } from '../../workspace/item-menu/useItemContextMenu';
import type { ItemDndProps } from '../../workspace/dnd/useItemDnd';
import { useIsDropTarget } from '../../workspace/dnd/dragState';
import { useIsCut } from '../../../stores/canvasClipboardStore';
import { InlineRenameField, useIsRenaming } from '../../workspace/rename/InlineRenameField';
import { TREE_ROW_HEIGHT_PX } from '../../workspace/layout';
import type { TreeRow } from './useFolderTreeRows';

export const TREE_INDENT_PX = 16;

// Focus as the browser's own mouse press would: no scroll in the virtualized
// list, and no focus ring after earlier keyboard use (script focus otherwise
// inherits the last modality). TS's FocusOptions lacks `focusVisible` yet.
const MOUSE_FOCUS: FocusOptions & { focusVisible?: boolean } = {
  preventScroll: true,
  focusVisible: false,
};

interface FolderTreeRowProps {
  readonly row: TreeRow;
  readonly index: number;
  readonly style: CSSProperties;
  /** Holds the tree's single tab stop. */
  readonly isActive: boolean;
  /** The folder the route has open. */
  readonly isCurrent: boolean;
  readonly menuProps: ItemContextMenuProps;
  readonly dndProps: ItemDndProps;
  readonly onOpen: (index: number) => void;
  readonly onToggle: (row: TreeRow) => void;
  readonly onFocusRow: (id: string) => void;
}

export function FolderTreeRow({
  row,
  index,
  style,
  isActive,
  isCurrent,
  menuProps,
  dndProps,
  onOpen,
  onToggle,
  onFocusRow,
}: FolderTreeRowProps) {
  const { node, item, level, isFolder, isExpanded } = row;
  const isDropTarget = useIsDropTarget(item.id);
  const renaming = useIsRenaming(item.id, 'tree');
  // Cut on the canvas, waiting for a paste: dimmed here too.
  const isCut = useIsCut(item.id);
  // The legacy tree's rule: the node carries a mode only when it has its own
  // privacy row, and the lock means nothing while ACLs are not enforced.
  const isRestricted = useIsAclEnforced() && node.privacyMode !== undefined;
  return (
    <Flex
      role="treeitem"
      aria-label={item.name}
      aria-level={level}
      aria-setsize={row.setSize}
      aria-posinset={row.posInSet}
      aria-expanded={isFolder ? isExpanded : undefined}
      aria-selected={isCurrent}
      tabIndex={isActive ? 0 : -1}
      data-row-index={index}
      data-cut={isCut ? '' : undefined}
      opacity={isCut ? 0.5 : undefined}
      style={style}
      align="center"
      gap={2}
      h={`${TREE_ROW_HEIGHT_PX}px`}
      pl={`${4 + (level - 1) * TREE_INDENT_PX}px`}
      pr={2}
      borderRadius="md"
      cursor="pointer"
      userSelect="none"
      color="text.primary"
      bg={isCurrent || isDropTarget ? 'sidebar.item.active' : 'transparent'}
      boxShadow={isDropTarget ? 'inset 0 0 0 2px var(--chakra-colors-border-focus)' : undefined}
      _hover={{ bg: isCurrent ? 'sidebar.item.active' : 'sidebar.item.hover' }}
      _focusVisible={{ outline: '2px solid', outlineColor: 'border.focus', outlineOffset: '-2px' }}
      onClick={() => onOpen(index)}
      onFocus={(event) => {
        if (event.target === event.currentTarget) onFocusRow(item.id);
      }}
      {...menuProps}
      {...dndProps}
    >
      <Box
        as="span"
        aria-hidden
        data-treeitem-action
        display="inline-flex"
        boxSize="16px"
        flexShrink={0}
        color="text.muted"
        visibility={isFolder ? 'visible' : 'hidden'}
        // The row is a native draggable, so a press that drifts past the drag
        // threshold here would start a drag and swallow the click (the legacy
        // drawer's #979 / #2134). Preventing the default blocks that drag and
        // the browser's focus move, so focus the row ourselves. Trade-off: a
        // row drag cannot start from the chevron.
        onMouseDown={(event: MouseEvent<HTMLElement>) => {
          event.preventDefault();
          event.currentTarget.closest<HTMLElement>('[role="treeitem"]')?.focus(MOUSE_FOCUS);
        }}
        onClick={(event: MouseEvent<HTMLElement>) => {
          event.stopPropagation();
          onToggle(row);
        }}
      >
        <MaterialIcon name={isExpanded ? 'expand_more' : 'chevron_right'} fontSize="16px" />
      </Box>
      <Glyph {...getHierarchyNodeGlyph(node.type, node.artifactType)} size="18px" gradient="warm" />
      {renaming ? (
        <InlineRenameField item={item} surface="tree" fontSize="14px" />
      ) : (
        <Text flex="1" minW={0} fontSize="14px" lineHeight="1.3" isTruncated title={item.name}>
          {item.name}
        </Text>
      )}
      {isRestricted && <RestrictedBadge mode={node.privacyMode} />}
      {isFolder && node.childCount > 0 && (
        <Text as="span" fontSize="12px" color="text.muted" flexShrink={0} aria-hidden>
          {node.childCount}
        </Text>
      )}
    </Flex>
  );
}
